import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { passwordResetAPI } from '../services/api';
import { FiKey, FiCheck, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import './Auth.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);


  useEffect(() => {
    const tokenParam = searchParams.get('token');

    if (!tokenParam) {
      setError('Invalid reset link. No token provided.');
      setValidating(false);
      return;
    }

    setToken(tokenParam);
    validateToken(tokenParam);
  }, [searchParams]);


  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    console.log('ResetPassword: Loading reCAPTCHA, site key:', siteKey ? 'Present' : 'Missing');

    if (!siteKey) {
      console.error('reCAPTCHA site key not configured');
      return;
    }

    // Check if script already loaded
    if (window.grecaptcha) {
      console.log('ResetPassword: reCAPTCHA already loaded');
      setRecaptchaReady(true);
      return;
    }

    // Load reCAPTCHA script
    console.log('ResetPassword: Loading reCAPTCHA script...');
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('ResetPassword: reCAPTCHA script loaded, waiting for ready...');
      const checkReady = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(() => {
            console.log('ResetPassword: reCAPTCHA ready!');
            setRecaptchaReady(true);
            clearInterval(checkReady);
          });
        }
      }, 100);
    };

    script.onerror = () => {
      console.error('ResetPassword: Failed to load reCAPTCHA script');
    };

    document.head.appendChild(script);

    // Cleanup
    return () => {
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript && !window.grecaptcha) {
        existingScript.remove();
      }
    };
  }, []);

  const validateToken = async (tokenValue) => {
    try {
      setValidating(true);
      setError('');

      const response = await passwordResetAPI.validateResetToken(tokenValue);

      if (response.data.valid) {
        setTokenValid(true);
        setUserInfo(response.data.user);
      } else {
        setTokenValid(false);
        setError(response.data.error || 'Invalid or expired reset token');
      }
    } catch (err) {
      setTokenValid(false);
      setError(err.response?.data?.error || 'Invalid or expired reset token');
    } finally {
      setValidating(false);
    }
  };

  const getRecaptchaToken = async () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    if (!siteKey || !window.grecaptcha) {
      console.warn('reCAPTCHA not configured');
      return null;
    }

    try {
      const token = await window.grecaptcha.execute(siteKey, { action: 'reset_password' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);

      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();

      const response = await passwordResetAPI.resetPassword({
        token,
        new_password: newPassword,
        recaptchaToken: recaptchaToken
      });

      if (response.data.success) {
        setSuccess(true);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while validating token
  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="spinner"></div>
            <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
              Validating reset token...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <FiAlertCircle size={64} style={{ color: '#ef4444' }} />
            <h1 style={{ margin: '16px 0 8px', color: '#ef4444' }}>Invalid Reset Link</h1>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              {error || 'This password reset link is invalid or has expired.'}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <FiCheck size={48} style={{ color: 'white' }} />
            </div>
            <h1 style={{ margin: '0 0 16px', color: '#10b981' }}>Password Reset Successfully!</h1>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
              Your password has been changed successfully.
            </p>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Redirecting to login page...
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <FiKey size={48} style={{ color: 'var(--primary)' }} />
          <h1 style={{ margin: '16px 0 8px' }}>Reset Password</h1>
          {userInfo && (
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
              Setting new password for <strong>{userInfo.full_name}</strong>
            </p>
          )}
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                required
                minLength={6}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '4px'
                }}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                required
                minLength={6}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  padding: '4px'
                }}
              >
                {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={!recaptchaReady || loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="recaptcha-notice" style={{ marginTop: '16px' }}>
          This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
